import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { ProgramNewsRunType } from "@/services/adminProgramNews";
import FullRunDialog from "./FullRunDialog";

interface RunButtonsProps {
  /** Which run is in flight (starting or listed as running); both buttons are disabled while either runs. */
  running: ProgramNewsRunType | null;
  /** Raw input text; parsed with parseLimit when a run starts so clearing the box is safe. */
  collectLimit: string;
  classifyLimit: string;
  onCollectLimitChange: (value: string) => void;
  onClassifyLimitChange: (value: string) => void;
  onCollect: () => void;
  onClassify: () => void;
  onCollectFull: () => void;
  onClassifyFull: () => void;
  /** Verified source count, for the full-collection estimate; null when not loaded. */
  sourceCount: number | null;
  /** Articles waiting for classification, for the full-classification estimate; null when not loaded. */
  waitingCount: number | null;
}

const RunButtons = ({
  running,
  collectLimit,
  classifyLimit,
  onCollectLimitChange,
  onClassifyLimitChange,
  onCollect,
  onClassify,
  onCollectFull,
  onClassifyFull,
  sourceCount,
  waitingCount,
}: RunButtonsProps) => {
  const busy = running !== null;
  const [confirming, setConfirming] = useState<ProgramNewsRunType | null>(null);

  function confirm() {
    const kind = confirming;
    setConfirming(null);
    if (kind === "collect") onCollectFull();
    else if (kind === "classify") onClassifyFull();
  }

  return (
    <div className="flex flex-wrap items-end gap-3">
      <label className="flex flex-col gap-1 text-xs text-muted-foreground">
        Sources per collection run
        <Input
          type="number"
          min={1}
          className="h-8 w-24"
          value={collectLimit}
          aria-label="Sources per collection run"
          disabled={busy}
          onChange={e => onCollectLimitChange(e.target.value)}
        />
      </label>
      <Button size="sm" onClick={onCollect} disabled={busy}>
        {running === "collect" ? (
          <>
            <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            Collecting…
          </>
        ) : (
          "Run collection"
        )}
      </Button>
      <Button
        size="sm"
        variant="ghost"
        className="border border-dashed"
        onClick={() => setConfirming("collect")}
        disabled={busy}
      >
        Run full collection
      </Button>
      <label className="flex flex-col gap-1 text-xs text-muted-foreground">
        Articles per classification run
        <Input
          type="number"
          min={1}
          className="h-8 w-24"
          value={classifyLimit}
          aria-label="Articles per classification run"
          disabled={busy}
          onChange={e => onClassifyLimitChange(e.target.value)}
        />
      </label>
      <Button size="sm" variant="outline" onClick={onClassify} disabled={busy}>
        {running === "classify" ? (
          <>
            <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            Classifying…
          </>
        ) : (
          "Run classification"
        )}
      </Button>
      <Button
        size="sm"
        variant="ghost"
        className="border border-dashed"
        onClick={() => setConfirming("classify")}
        disabled={busy}
      >
        Run full classification
      </Button>
      <FullRunDialog
        kind={confirming ?? "collect"}
        count={confirming === "classify" ? waitingCount : sourceCount}
        open={confirming !== null}
        onConfirm={confirm}
        onCancel={() => setConfirming(null)}
      />
      <span className="text-xs text-muted-foreground">
        Runs start right away and the table below updates while they go; keep manual runs small. The
        nightly job runs everything.
      </span>
    </div>
  );
};

export default RunButtons;
